import {
  type AccountListEntry,
  type BucketEntry,
  bucketsList,
  childDeactivate,
  historyList,
  pendingChoresList,
  pendingDepositsList,
  pendingTransfersList,
  pendingWithdrawalsList,
} from '@chorepop/core/client';
import { type Component, For, Show, createMemo, createResource, createSignal } from 'solid-js';
import { ReportContentButton } from '../components/ReportContentButton';
import { TransientBanner } from '../components/TransientBanner';
import { EditGuardianTypeDialog } from './EditGuardianTypeDialog';

type Props = {
  account: AccountListEntry;
  sessionId: string;
  childAccountId: number;
  childDisplayName: string;
  flashMessage?: string | undefined;
  onFlashDismiss?: (() => void) | undefined;
  onBack: () => void;
  onChildDeactivated: (childDisplayName: string, purgeAfterUtc: string) => void;
  onViewPendingApprovalsForChild: (childAccountId: number) => void;
  onRecordDeposit: (childAccountId: number, childDisplayName: string) => void;
  onRecordGift: (childAccountId: number, childDisplayName: string) => void;
  onManageAllowances: (childAccountId: number, childDisplayName: string) => void;
  onViewHistory: (childAccountId: number, childDisplayName: string) => void;
  onViewChores: (childAccountId: number, childDisplayName: string) => void;
  onViewStreaks: (childAccountId: number, childDisplayName: string) => void;
  onResetChildPassword: (childAccountId: number, childDisplayName: string) => void;
  onDeactivateBucket: (bucket: BucketEntry, existingBuckets: BucketEntry[]) => void;
  onAdjustBucketBalance: (bucket: BucketEntry) => void;
  onDirectTransfer: (
    childAccountId: number,
    childDisplayName: string,
    buckets: BucketEntry[],
  ) => void;
  onManageBuckets: (
    childAccountId: number,
    childDisplayName: string,
    buckets: BucketEntry[],
  ) => void;
};

function formatMoney(minorUnits: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(
      minorUnits / 100,
    );
  } catch {
    return `${currencyCode} ${(minorUnits / 100).toFixed(2)}`;
  }
}

function formatActivityTime(atUtc: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(atUtc));
  } catch {
    return atUtc;
  }
}

/**
 * The direct Claude Design child-detail destination. It deliberately does not
 * reuse the old guardian-management component: a child account is a concise
 * balance-and-buckets view, with the account actions available in one row.
 */
export const ChildDetailScreen: Component<Props> = (props) => {
  const [manageOpen, setManageOpen] = createSignal(false);
  const [confirmDeactivate, setConfirmDeactivate] = createSignal(false);
  const [deleteConfirmationName, setDeleteConfirmationName] = createSignal('');
  const [deactivating, setDeactivating] = createSignal(false);
  const [manageError, setManageError] = createSignal<string | null>(null);
  const [editingRelationship, setEditingRelationship] = createSignal(false);
  const [relationshipFlash, setRelationshipFlash] = createSignal<string | null>(null);
  const [bucketsResource] = createResource(async () => {
    const result = await bucketsList({ sessionId: props.sessionId });
    return result.ok ? result.value : [];
  });
  const [historyResource] = createResource(async () => {
    const result = await historyList({
      sessionId: props.sessionId,
      childAccountId: props.childAccountId,
      limit: 12,
    });
    return result.ok ? result.value : [];
  });
  const [pendingResource] = createResource(async () => {
    const [deposits, withdrawals, chores, transfers] = await Promise.all([
      pendingDepositsList({ sessionId: props.sessionId }),
      pendingWithdrawalsList({ sessionId: props.sessionId }),
      pendingChoresList({ sessionId: props.sessionId }),
      pendingTransfersList({ sessionId: props.sessionId }),
    ]);
    return [deposits, withdrawals, chores, transfers].reduce((total, result) => {
      if (!result.ok) return total;
      return (
        total + result.value.filter((entry) => entry.childAccountId === props.childAccountId).length
      );
    }, 0);
  });

  const buckets = createMemo(() =>
    (bucketsResource() ?? [])
      .filter((bucket) => bucket.childAccountId === props.childAccountId)
      .sort((left, right) => left.sortOrder - right.sortOrder),
  );
  const totalBalance = createMemo(() =>
    buckets().reduce((total, bucket) => total + bucket.balanceMinorUnits, 0),
  );
  const pendingCount = () => pendingResource() ?? 0;
  const deactivateChild = async () => {
    setDeactivating(true);
    setManageError(null);
    const result = await childDeactivate({
      sessionId: props.sessionId,
      childAccountId: props.childAccountId,
    });
    setDeactivating(false);
    if (!result.ok) {
      setManageError(result.error.message);
      return;
    }
    props.onChildDeactivated(props.childDisplayName, result.value.purgeAfterUtc);
  };
  const recoveryDeadline = () =>
    new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'long', year: 'numeric' }).format(
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
    );

  return (
    <main class="child-detail-screen">
      <header class="child-detail-header">
        <button
          type="button"
          class="child-detail-back"
          onClick={() => props.onBack()}
          aria-label="Back to family overview"
        >
          ←
        </button>
        <div>
          <span class="action-screen-eyebrow">Child account</span>
          <h1>{props.childDisplayName}</h1>
          <p>
            {buckets().length} buckets · {pendingCount()} waiting
          </p>
        </div>
        <div class="child-detail-manage">
          <button
            type="button"
            class="child-detail-manage-trigger"
            aria-label={`Manage ${props.childDisplayName}`}
            aria-expanded={manageOpen()}
            aria-haspopup="menu"
            onClick={() => {
              setManageOpen((open) => !open);
              setConfirmDeactivate(false);
              setDeleteConfirmationName('');
              setManageError(null);
            }}
          >
            •••
          </button>
          <Show when={manageOpen()}>
            <div class="child-detail-manage-menu" role="menu" aria-label="Child management">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setManageOpen(false);
                  props.onResetChildPassword(props.childAccountId, props.childDisplayName);
                }}
              >
                Reset password
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setManageOpen(false);
                  setRelationshipFlash(null);
                  setEditingRelationship(true);
                }}
              >
                Change relationship
              </button>
              <ReportContentButton
                class="child-detail-report-menuitem"
                label="Report this profile"
                role="menuitem"
                onHide={props.onBack}
                context={{
                  contentKind: 'account',
                  contentLocalId: `account:${props.childAccountId}`,
                  reportedLocalAccountId: props.childAccountId,
                  label: `${props.childDisplayName}'s profile`,
                  contentSnapshot: {
                    accountId: props.childAccountId,
                    displayName: props.childDisplayName,
                    roleCode: 'child',
                  },
                }}
              />
              <button
                type="button"
                role="menuitem"
                class="is-danger"
                onClick={() => {
                  setManageOpen(false);
                  setDeleteConfirmationName('');
                  setConfirmDeactivate(true);
                }}
                data-testid={`child-deactivate-${props.childAccountId}`}
              >
                Delete child account
              </button>
            </div>
          </Show>
        </div>
      </header>

      <Show when={props.flashMessage}>
        {(message) => (
          <TransientBanner
            class="home-flash"
            message={message()}
            onDismiss={props.onFlashDismiss}
          />
        )}
      </Show>
      <Show when={relationshipFlash()}>
        {(message) => (
          <TransientBanner
            class="home-flash"
            message={message()}
            onDismiss={() => setRelationshipFlash(null)}
          />
        )}
      </Show>

      <section class="child-detail-body">
        <Show when={confirmDeactivate()}>
          <section
            class="child-detail-confirm"
            role="alertdialog"
            aria-labelledby="deactivate-child-title"
          >
            <strong id="deactivate-child-title">Delete {props.childDisplayName}'s account?</strong>
            <p>
              This removes {props.childDisplayName} and their chores, buckets, goals, and money
              history from this ChorePop family. You have 30 days to reclaim the account and its
              data (until {recoveryDeadline()}). After that, it is permanently deleted.
            </p>
            <label>
              Type <strong>{props.childDisplayName}</strong> to continue
              <input
                type="text"
                value={deleteConfirmationName()}
                onInput={(event) => setDeleteConfirmationName(event.currentTarget.value)}
                autocomplete="off"
                aria-label={`Type ${props.childDisplayName} to confirm deletion`}
              />
            </label>
            <div>
              <button
                type="button"
                onClick={() => {
                  setConfirmDeactivate(false);
                  setDeleteConfirmationName('');
                }}
                disabled={deactivating()}
              >
                Cancel
              </button>
              <button
                type="button"
                class="is-danger"
                onClick={() => void deactivateChild()}
                disabled={
                  deactivating() || deleteConfirmationName().trim() !== props.childDisplayName
                }
              >
                {deactivating() ? 'Deleting…' : 'Delete account'}
              </button>
            </div>
            <Show when={manageError()}>{(message) => <p role="alert">{message()}</p>}</Show>
          </section>
        </Show>
        <section class="child-detail-balance" aria-label={`${props.childDisplayName}'s balance`}>
          <div>
            <span class="action-screen-eyebrow">Total balance</span>
            <strong>{formatMoney(totalBalance(), props.account.displayCurrencyCode)}</strong>
          </div>
          <Show when={pendingCount() > 0}>
            <button
              type="button"
              class="child-detail-waiting"
              onClick={() => props.onViewPendingApprovalsForChild(props.childAccountId)}
            >
              {pendingCount()} waiting
            </button>
          </Show>
          <div class="child-detail-split" aria-label="Bucket split">
            <For each={buckets()}>
              {(bucket) => (
                <span
                  style={`--bucket-share: ${Math.max(1, bucket.allocationBps)}; --bucket-color: ${bucket.displayColor ?? 'var(--brand)'}`}
                  title={`${bucket.bucketName}: ${Math.round(bucket.allocationBps / 100)}%`}
                />
              )}
            </For>
          </div>
        </section>

        <nav class="child-detail-actions" aria-label={`${props.childDisplayName} actions`}>
          <button
            type="button"
            onClick={() => props.onRecordDeposit(props.childAccountId, props.childDisplayName)}
          >
            Record deposit
          </button>
          <button
            type="button"
            onClick={() => props.onRecordGift(props.childAccountId, props.childDisplayName)}
          >
            Record gift
          </button>
          <button
            type="button"
            onClick={() =>
              props.onManageBuckets(props.childAccountId, props.childDisplayName, buckets())
            }
          >
            Buckets
          </button>
          <button
            type="button"
            onClick={() => props.onViewChores(props.childAccountId, props.childDisplayName)}
          >
            Chores
          </button>
          <button
            type="button"
            onClick={() => props.onManageAllowances(props.childAccountId, props.childDisplayName)}
          >
            Allowances
          </button>
          <button
            type="button"
            onClick={() => props.onViewStreaks(props.childAccountId, props.childDisplayName)}
            data-testid="child-detail-streaks"
          >
            Streaks
          </button>
        </nav>

        <div class="child-detail-grid">
          <section class="child-detail-card">
            <div class="child-detail-card-heading">
              <span class="action-screen-eyebrow">Buckets</span>
              <button
                type="button"
                onClick={() =>
                  props.onManageBuckets(props.childAccountId, props.childDisplayName, buckets())
                }
              >
                Edit
              </button>
            </div>
            <Show
              when={!bucketsResource.loading}
              fallback={<p class="child-detail-empty">Loading buckets…</p>}
            >
              <Show
                when={buckets().length > 0}
                fallback={<p class="child-detail-empty">No buckets yet.</p>}
              >
                <ul class="child-detail-bucket-list">
                  <For each={buckets()}>
                    {(bucket) => (
                      <li>
                        <span
                          class="child-detail-bucket-dot"
                          style={`background: ${bucket.displayColor ?? 'var(--brand)'}`}
                          aria-hidden="true"
                        />
                        <span>
                          <strong>{bucket.bucketName}</strong>
                          <small>{Math.round(bucket.allocationBps / 100)}% of deposits</small>
                          <Show
                            when={bucket.requiresApprovalToWithdraw || bucket.isGuardianManaged}
                          >
                            <span class="child-detail-bucket-flags">
                              <Show when={bucket.requiresApprovalToWithdraw}>
                                <span title="Requires guardian approval to withdraw">
                                  Approval required
                                </span>
                              </Show>
                              <Show when={bucket.isGuardianManaged}>
                                <span title="Guardian-managed">Guardian managed</span>
                              </Show>
                            </span>
                          </Show>
                        </span>
                        <strong>
                          {formatMoney(bucket.balanceMinorUnits, props.account.displayCurrencyCode)}
                        </strong>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </Show>
          </section>

          <section class="child-detail-card child-detail-recent">
            <div class="child-detail-card-heading">
              <span class="action-screen-eyebrow">Recent activity</span>
              <button
                type="button"
                onClick={() => props.onViewHistory(props.childAccountId, props.childDisplayName)}
              >
                See all
              </button>
            </div>
            <Show
              when={!historyResource.loading}
              fallback={<p class="child-detail-empty">Loading activity…</p>}
            >
              <Show
                when={(historyResource() ?? []).length > 0}
                fallback={<p class="child-detail-empty">No activity yet.</p>}
              >
                <ul class="child-detail-activity-list">
                  <For each={historyResource()}>
                    {(entry) => {
                      const isDeposit = entry.kind === 'deposit';
                      const isWithdrawal = entry.kind === 'withdrawal';
                      const label = isDeposit
                        ? `Deposit · ${entry.sourceLabel}`
                        : isWithdrawal
                          ? `Withdrawal · ${entry.reasonLabel}`
                          : `Transfer · ${entry.sourceBucketName} → ${entry.targetBucketName ?? 'deactivated bucket'}`;
                      const signedAmount = `${isDeposit ? '+' : isWithdrawal ? '−' : '↔'}${formatMoney(entry.amountMinorUnits, props.account.displayCurrencyCode)}`;
                      return (
                        <li>
                          <span
                            class={`child-detail-activity-icon is-${entry.kind}`}
                            aria-hidden="true"
                          >
                            {isDeposit ? '+' : isWithdrawal ? '−' : '↔'}
                          </span>
                          <span>
                            <strong>{label}</strong>
                            <small>{formatActivityTime(entry.atUtc)}</small>
                          </span>
                          <strong
                            class={isDeposit ? 'is-positive' : isWithdrawal ? 'is-negative' : ''}
                          >
                            {signedAmount}
                          </strong>
                        </li>
                      );
                    }}
                  </For>
                </ul>
              </Show>
            </Show>
          </section>
        </div>
      </section>
      <EditGuardianTypeDialog
        when={
          editingRelationship()
            ? {
                childAccountId: props.childAccountId,
                childDisplayName: props.childDisplayName,
              }
            : null
        }
        sessionId={props.sessionId}
        onSaved={(childName, newTypeLabel) => {
          setEditingRelationship(false);
          setRelationshipFlash(`Relationship with ${childName} set to ${newTypeLabel}.`);
        }}
        onCancel={() => setEditingRelationship(false)}
      />
    </main>
  );
};
